import mqttService from "@/src/services/mqtt/mqttService.js";
import { Topics, topicManager } from "./topicManager.js";
import { eventManager } from "./eventManager.js";
import { stopHeartbeat, startHeartbeat, fetchBulkPresence, sendOfflineBeacon } from "./presenceService.js";
import store from "@/src/store/index.js";
import eventBus from "@/src/utils/eventBus.js";

class RealtimeClient {
  constructor() {
    this.workspaceId = null;
    this.userId = null;
    this.currentGroupId = null;
    this._threadGroupId = null;
    this.subscriptionQueue = Promise.resolve();
    this._boundHandleMessage = this.handleMessage.bind(this);
    this._huddleSubscribed = false;
    this._presenceSubscribed = false;
    this.setupMQTTEventHandlers();
  }

  setupMQTTEventHandlers() {
    console.log("[RealtimeClient] Setting up MQTT event handlers");

    eventBus.on("mqtt-connected", async () => {
      if (!localStorage.getItem("huddle")) return;
      console.log("[RealtimeClient] 🟢 MQTT connected, subscribing to huddle topics");
      await this.subscribeToHuddleTopics();
    });

    eventBus.on("mqtt-reconnected", async () => {
      console.log("[RealtimeClient] 🔄 MQTT reconnected, re-subscribing to topics");
      await this.resubscribeAll();
    });

    console.log("[RealtimeClient] ✅ MQTT event handlers registered");
  }

  async resubscribeAll() {
    const savedGroupId = this.currentGroupId;
    const savedThreadGroupId = this._threadGroupId;
    const wasHuddleSubscribed = this._huddleSubscribed;
    const wasPresenceSubscribed = this._presenceSubscribed;

    console.log("[RealtimeClient] Re-subscribing to all topics after reconnect", {
      hasUserId: !!this.userId,
      hasCurrentGroupId: !!savedGroupId,
      hasThreadGroupId: !!savedThreadGroupId,
      wasHuddleSubscribed,
      wasPresenceSubscribed,
    });

    this.currentGroupId = null;
    this._threadGroupId = null;
    this._huddleSubscribed = false;
    this._presenceSubscribed = false;
    topicManager.clear();

    if (this.userId) {
      await this.subscribeToUserSidebar(this.userId);
    }

    if (savedGroupId) {
      await this.subscribeToGroup(savedGroupId);
      // Restore group state even if subscribe failed (e.g. connection dropped again)
      // so the next reconnect can retry
      if (!this.currentGroupId) {
        this.currentGroupId = savedGroupId;
      }
    }

    if (savedThreadGroupId) {
      await this.subscribeToThread(savedThreadGroupId);
      // Restore thread state even if subscribe failed (e.g. connection dropped again)
      // so the next reconnect can retry
      if (!this._threadGroupId) {
        this._threadGroupId = savedThreadGroupId;
      }
    }

    if (wasHuddleSubscribed) {
      await this.subscribeToHuddleTopics();
    }

    if (wasPresenceSubscribed) {
      await this.subscribeToPresence();
    }
  }

  async setWorkspaceAndUser(workspaceId, userId) {
    const oldWorkspaceId = this.workspaceId;
    const isWorkspaceChange = oldWorkspaceId && workspaceId && String(oldWorkspaceId) !== String(workspaceId);

    const isPresenceEnabled = !!localStorage.getItem("chat_presence");

    if (isWorkspaceChange && isPresenceEnabled) {
      sendOfflineBeacon();
      stopHeartbeat();
      await this.unsubscribeFromPresence();
      store.commit("users/CLEAR_PRESENCE");
    }

    this.workspaceId = workspaceId || null;
    this.userId = userId || null;

    if (isWorkspaceChange && workspaceId && userId && isPresenceEnabled) {
      await this.subscribeToPresence();
      fetchBulkPresence();
      startHeartbeat();
    }
  }

  async subscribeToGroup(groupId) {
    this.subscriptionQueue = this.subscriptionQueue
      .then(async () => {

        if (!this.workspaceId || !groupId) {
          return;
        }

        if (!mqttService.isConnected) {
          return;
        }

        if (this.currentGroupId === groupId) {
          return;
        }

        if (this.currentGroupId && this.currentGroupId !== groupId) {
          await this._unsubscribeFromGroupInternal(this.currentGroupId);
        }

        const messagesTopic = Topics.groupMessages(this.workspaceId, groupId);
        const typingTopic = Topics.groupTyping(this.workspaceId, groupId);

        topicManager.subscribe(messagesTopic, this._boundHandleMessage);
        topicManager.subscribe(typingTopic, this._boundHandleMessage);

        await mqttService.subscribe(messagesTopic, this._boundHandleMessage);
        await mqttService.subscribe(typingTopic, this._boundHandleMessage);

        this.currentGroupId = groupId;
      })
      .catch((error) => {
        console.error("Failed to subscribe to group:", error);
      });

    return this.subscriptionQueue;
  }

  async _unsubscribeFromGroupInternal(groupId) {
    if (!this.workspaceId || !groupId) return;

    const messagesTopic = Topics.groupMessages(this.workspaceId, groupId);
    const typingTopic = Topics.groupTyping(this.workspaceId, groupId);
    const threadsTopic = Topics.groupThreads(this.workspaceId, groupId);

    const shouldUnsubMessages = topicManager.unsubscribe(
      messagesTopic,
      this._boundHandleMessage,
    );
    const shouldUnsubTyping = topicManager.unsubscribe(
      typingTopic,
      this._boundHandleMessage,
    );
    const shouldUnsubThreads = topicManager.unsubscribe(
      threadsTopic,
      this._boundHandleMessage,
    );

    if (shouldUnsubMessages) {
      await mqttService.unsubscribe(messagesTopic);
    }
    if (shouldUnsubTyping) {
      await mqttService.unsubscribe(typingTopic);
    }
    if (shouldUnsubThreads) {
      await mqttService.unsubscribe(threadsTopic);
      if (this._threadGroupId === groupId) {
        this._threadGroupId = null;
      }
    }
  }

  async unsubscribeFromGroup(groupId) {
    this.subscriptionQueue = this.subscriptionQueue
      .then(async () => {
        await this._unsubscribeFromGroupInternal(groupId);
        if (this.currentGroupId === groupId) {
          this.currentGroupId = null;
        }
      })
      .catch((error) => {
        console.error("Failed to unsubscribe from group:", error);
      });

    return this.subscriptionQueue;
  }

  async subscribeToThread(groupId) {
    if (!this.workspaceId || !groupId) return;
    if (!mqttService.isConnected) return;

    this._threadGroupId = groupId;
    const topic = Topics.groupThreads(this.workspaceId, groupId);
    const shouldSubscribe = topicManager.subscribe(
      topic,
      this._boundHandleMessage,
    );

    if (shouldSubscribe) {
      await mqttService.subscribe(topic, this._boundHandleMessage);
    }
  }

  async unsubscribeFromThread(groupId) {
    if (!this.workspaceId || !groupId) return;

    if (this._threadGroupId === groupId) {
      this._threadGroupId = null;
    }
    const topic = Topics.groupThreads(this.workspaceId, groupId);
    const shouldUnsubscribe = topicManager.unsubscribe(
      topic,
      this._boundHandleMessage,
    );

    if (shouldUnsubscribe) {
      await mqttService.unsubscribe(topic);
    }
  }

  handleMessage(message, topic) {
    if (!message) {
      return;
    }

    eventManager.dispatch(message);
  }

  async publish(topic, message, options = {}) {
    try {
      await mqttService.publish(topic, message, options);
    } catch (error) {
      console.error("Failed to publish message:", error);
    }
  }

  async subscribeToUserSidebar(userId) {
    if (!this.workspaceId || !userId) {
      return;
    }

    if (!mqttService.isConnected) {
      return;
    }

    const topic = Topics.userSidebar(this.workspaceId, userId);

    topicManager.subscribe(topic, this._boundHandleMessage);
    await mqttService.subscribe(topic, this._boundHandleMessage);
  }

  async subscribeToHuddleTopics() {
    if (!this.workspaceId) return;
    if (!mqttService.isConnected) return;
    if (this._huddleSubscribed) return;

    const huddleTopic = Topics.huddleWildcard(this.workspaceId);
    const huddleParticipantsTopic = Topics.huddleParticipantsWildcard(this.workspaceId);

    topicManager.subscribe(huddleTopic, this._boundHandleMessage);
    topicManager.subscribe(huddleParticipantsTopic, this._boundHandleMessage);

    await Promise.all([
      mqttService.subscribe(huddleTopic, this._boundHandleMessage),
      mqttService.subscribe(huddleParticipantsTopic, this._boundHandleMessage),
    ]);

    this._huddleSubscribed = true;
    console.log("[RealtimeClient] Subscribed to huddle wildcard topics");
  }

  async subscribeToPresence() {
    if (!this.workspaceId) return;
    if (!mqttService.isConnected) return;
    if (this._presenceSubscribed) return;

    const topic = Topics.workspacePresence(this.workspaceId);
    topicManager.subscribe(topic, this._boundHandleMessage);
    await mqttService.subscribe(topic, this._boundHandleMessage);

    this._presenceSubscribed = true;
  }

  async unsubscribeFromPresence() {
    if (!this.workspaceId || !this._presenceSubscribed) return;

    const topic = Topics.workspacePresence(this.workspaceId);
    const shouldUnsub = topicManager.unsubscribe(topic, this._boundHandleMessage);
    if (shouldUnsub) {
      await mqttService.unsubscribe(topic);
    }
    this._presenceSubscribed = false;
  }

  async unsubscribeFromHuddleTopics() {
    if (!this.workspaceId || !this._huddleSubscribed) return;

    const huddleTopic = Topics.huddleWildcard(this.workspaceId);
    const huddleParticipantsTopic = Topics.huddleParticipantsWildcard(this.workspaceId);

    const shouldUnsubHuddle = topicManager.unsubscribe(huddleTopic, this._boundHandleMessage);
    const shouldUnsubParticipants = topicManager.unsubscribe(huddleParticipantsTopic, this._boundHandleMessage);

    if (shouldUnsubHuddle) {
      await mqttService.unsubscribe(huddleTopic);
    }
    if (shouldUnsubParticipants) {
      await mqttService.unsubscribe(huddleParticipantsTopic);
    }

    this._huddleSubscribed = false;
  }

  async unsubscribeFromUserSidebar(userId) {
    if (!this.workspaceId || !userId) return;

    const topic = Topics.userSidebar(this.workspaceId, userId);
    const shouldUnsubscribe = topicManager.unsubscribe(
      topic,
      this._boundHandleMessage,
    );

    if (shouldUnsubscribe) {
      await mqttService.unsubscribe(topic);
    }
  }

  async disconnect() {
    try {
      if (this._presenceSubscribed) {
        sendOfflineBeacon();
        stopHeartbeat();
      }
      if (this.currentGroupId) {
        await this.unsubscribeFromGroup(this.currentGroupId);
      }
      if (this.userId) {
        await this.unsubscribeFromUserSidebar(this.userId);
      }
      await mqttService.disconnect();
      topicManager.clear();
      store.commit("users/CLEAR_PRESENCE");
      this.workspaceId = null;
      this.userId = null;
      this.currentGroupId = null;
      this._threadGroupId = null;
      this._huddleSubscribed = false;
      this._presenceSubscribed = false;
      console.log("Disconnected from MQTT and cleared topic subscriptions");
    } catch (error) {
      console.error("Failed to disconnect from MQTT:", error);
    }
  }
}

export const realtimeClient = new RealtimeClient();
